import { useCallback, useState } from "react";
import { usePaginationSortSearch } from "containers/Table/hooks/usePaginationSortSearch";
import { Paginar } from "_utils/enuns/Paginar.enum";
import { IFiltrosListarSolicitacoesLicenciamento } from "features/core/pages/Backoffice/Pages/Licenciamento/Interfaces/IFiltrosListarSolicitacoesLicenciamento";
import { AxiosRequest } from "_services/api";
import { HttpResponse } from "_services/api/HttpResponse";
import {
  IData,
  IHttpError,
  IHttpResponse,
} from "_services/api/interface/HttpResponse";
import { HttpError } from "_services/api/HttpError";
import { IProcessoVincularLoteLicenciamentos } from "features/core/pages/Backoffice/Pages/Licenciamento/Interfaces/IProcessoVincularLoteLicenciamentos";
import { IFiltroProcessoCriarLoteLicenciamentos } from "features/core/pages/Backoffice/Pages/Licenciamento/Interfaces/IFiltroProcessoCriarLoteLicenciamentos";
import { toastAtencao, toastErro } from "components/Toast";
import { IListaLicenciamentos } from "features/core/pages/Backoffice/Pages/Licenciamento/DetalhesLicenciamento/interfaces/IListaLicenciamentos";
import { IFiltrosListarLicenciamentos } from "features/core/pages/Backoffice/Pages/Licenciamento/DetalhesLicenciamento/interfaces/IFiltrosListarLicenciamentos";
import { IDadosLoteLicenciamento } from "features/core/pages/Backoffice/Pages/Licenciamento/DetalhesLicenciamento/interfaces/IDadosLoteLicenciamento";
import { getToken } from "_services/auth";
import { IEmissaoManual } from "features/core/pages/Backoffice/Pages/Licenciamento/DetalhesLicenciamento/interfaces/IEmissaoManual";
import { converterDataEua, converterDataHoraEua } from "_utils/Data";
import { IDetalhesEmissaoExportar } from "features/core/pages/Backoffice/Pages/Licenciamento/Interfaces/IDetalhesEmissaoExportar";
import { IDadosListarLoteLicenciamento } from "features/core/pages/Backoffice/Pages/Licenciamento/Containers/ListagemSolicitacoesLicenciamento/containers/Tabela";
import { ISalvarlote } from "features/core/pages/Backoffice/Pages/Licenciamento/Interfaces/ISalvarLote";

export function useSolicitacaoLicenciamentos() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<IHttpResponse<IData | any>>();
  const msAw = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);
  const api = AxiosRequest.makeApi();
  const [filtros, setFiltros] =
    useState<IFiltrosListarSolicitacoesLicenciamento>(
      {} as IFiltrosListarSolicitacoesLicenciamento,
    );
  const [solicitacoes, setSolicitacoes] = useState<
    IDadosListarLoteLicenciamento[]
  >([]);
  const { totalRows, setTotalRows } = usePaginationSortSearch();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedRowsDetalhes, setSelectedRowsDetalhes] = useState<any[]>([]);
  const [clearRows, setClearRows] = useState<boolean>(false);
  const [clearRowsDetalhes, setClearRowsDetalhes] = useState<boolean>(false);
  const [selectedRowsRemover, setSelectedRowsRemover] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [processosVincularLote, setProcessosVincularLote] = useState<
    IProcessoVincularLoteLicenciamentos[]
  >([]);
  const [listaDetalhesLicenciamentos, setListaDetalhesLicenciamentos] =
    useState<IListaLicenciamentos[]>([]);
  const [erroReemitir, setErroReemitir] = useState<boolean>(false);
  const [mensagemErroReemitir, setMensagemErroReemitir] = useState<string>("");
  const [filtrosDetalhes, setFiltrosDetalhes] =
    useState<IFiltrosListarLicenciamentos>({} as IFiltrosListarLicenciamentos);
  const [anoSolicitado, setAnoSolicitado] = useState<number>(0);
  const [dadosExportar, setDadosExportar] = useState<
    IDetalhesEmissaoExportar[]
  >([]);

  const gerarQueryParams = useCallback(() => {
    const filtrosArray = [
      filtros.placa ? `placa=${filtros.placa}` : null,
      filtros.renavam ? `renavam=${filtros.renavam}` : null,
      filtros.idEmpresa ? `idEmpresa=${filtros.idEmpresa}` : null,

      filtros.periodoSolicitacao && filtros.periodoSolicitacao[0]
        ? `inicioDataSolicitacao=${converterDataEua(filtros.periodoSolicitacao[0])}`
        : null,
      filtros.periodoSolicitacao &&
      filtros.periodoSolicitacao[0] &&
      filtros.periodoSolicitacao[1]
        ? `fimDataSolicitacao=${converterDataEua(filtros.periodoSolicitacao[1])}`
        : null,
      filtros.statusEmissao ? `statusEmissao=${filtros.statusEmissao}` : null,
      filtros.uf ? `uf=${filtros.uf}` : null,
      filtros.cobranca ? `cobranca=${filtros.cobranca}` : null,
    ].filter((item) => item !== null);

    return `${filtrosArray.join("&")}`;
  }, [filtros]);

  const listarSolicitacoesLicenciamento = useCallback(
    async (paginar: number = Paginar.SIM) => {
      setClearRows(true);
      setLoading(true);
      try {
        let params = gerarQueryParams();

        const { data } = await msAw.get(
          `/licenciamento/lote?${params}&pagina=${page}&limite=${paginar == Paginar.SIM ? limit : 0}`,
        );

        setSolicitacoes(data.itens);
        setTotalRows(data.meta.totalItems);
        return data.itens;
      } catch (error) {
        setSolicitacoes([]);
        return [];
      } finally {
        setLoading(false);
        setClearRows(false);
      }
    },
    [gerarQueryParams, setTotalRows, setSolicitacoes, setLoading, limit, page],
  );

  const salvarLote = async (dados: ISalvarlote) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.post(`/licenciamento/lote`, dados);

      const response = new HttpResponse(false, status, data);
      setError(response);

      listarSolicitacoesLicenciamento();
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const validarProcessosVincularLote = async (processos: number[]) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(
        `/processo/validar/?processos=${JSON.stringify(processos)}`,
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const validarPlacasChassisVincularLote = async (
    placasChassisRenavams: string[],
  ) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(
        `/processo/veiculo/validar/?placasChassisRenavams=${JSON.stringify(
          placasChassisRenavams,
        )}`,
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const getParamsFiltroListarProcessosVincularLote = (
    filtros: IFiltroProcessoCriarLoteLicenciamentos,
    idEmpresa: number,
  ) => {
    const obj: any = { idEmpresa };

    if (filtros.servicoProcesso) {
      obj.servicoProcesso = filtros.servicoProcesso;
    }

    if (filtros.processos && filtros.processos.length) {
      obj.processos = JSON.stringify(filtros.processos);
    }

    if (filtros.placasChassisRenavams && filtros.placasChassisRenavams.length) {
      obj.placasChassisRenavams = JSON.stringify(filtros.placasChassisRenavams);
    }

    let params = Object.keys(obj)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`,
      )
      .join("&");

    if (
      params &&
      (!filtros.placasChassisRenavams ||
        !filtros.placasChassisRenavams.length) &&
      (!filtros.processos || !filtros.processos.length)
    ) {
      params = `${params}&ultimosXDias=90`;
    }

    return params;
  };

  const listarProcessosVincularLote = async (
    dados: IFiltroProcessoCriarLoteLicenciamentos,
    idEmpresa: number,
  ) => {
    const params = getParamsFiltroListarProcessosVincularLote(dados, idEmpresa);

    if (!params) {
      toastAtencao("Selecione pelo menos um filtro");
      return;
    }

    setLoading(true);
    setClearRows(true);
    try {
      const { status, data } = await msAw.get(
        `/processo/vincular/licenciamentos?${params}`,
      );

      setProcessosVincularLote(data);
      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      setProcessosVincularLote([]);
      return response;
    } finally {
      setLoading(false);
      setClearRows(false);
    }
  };

  const gerarQueryParamsDetalhes = (dados: IFiltrosListarLicenciamentos) => {
    setFiltrosDetalhes(dados);
    const filtrosArray = [
      dados.placa ? `placa=${dados.placa}` : null,
      dados.renavam ? `renavam=${dados.renavam}` : null,
      dados.exercicio ? `exercicio=${dados.exercicio}` : null,
      dados.statusEmissao ? `statusEmissao=${dados.statusEmissao}` : null,
    ].filter((item) => item !== null);

    return `${filtrosArray.join("&")}`;
  };

  const listarDetalhesLicenciamento = useCallback(
    async (
      idLote: number,
      dados?: IFiltrosListarLicenciamentos,
      paginar: number = Paginar.SIM,
    ) => {
      setClearRowsDetalhes(true);
      setLoading(true);

      try {
        let params = dados ? gerarQueryParamsDetalhes(dados) : "";

        const { data } = await msAw.get(
          `/licenciamento/item/detalhes/${idLote}?${params}&pagina=${page}&limite=${paginar == Paginar.SIM ? limit : 0}`,
        );

        setListaDetalhesLicenciamentos(data.itens);
        setTotalRows(data.meta.totalItems);
        setAnoSolicitado(data.itens[0].exercicioSolicitado);
        return data.itens;
      } catch (error) {
        setListaDetalhesLicenciamentos([]);
        setAnoSolicitado(0);
        return [];
      } finally {
        setLoading(false);
        setClearRowsDetalhes(false);
      }
    },
    [
      gerarQueryParamsDetalhes,
      setTotalRows,
      setListaDetalhesLicenciamentos,
      setLoading,
      limit,
      page,
    ],
  );

  const defaultDadosLote: IDadosLoteLicenciamento = {
    id: 0,
    exercicio: 0,
    uf: "",
    nomeEmpresa: "",
    descricao: "",
    cpfCnpj: "",
  };

  const [dadosLote, setDadosLote] =
    useState<IDadosLoteLicenciamento>(defaultDadosLote);

  const buscarDadosLicenciamento = useCallback(
    async (idLote: number) => {
      setLoading(true);
      try {
        const { data } = await msAw.get(`/licenciamento/lote/${idLote}`);

        setDadosLote({
          id: idLote,
          exercicio: data.exercicio,
          uf: data.uf,
          nomeEmpresa: data.nomeEmpresa,
          descricao: data.descricao,
          cpfCnpj: data.cpfCnpj,
        });
      } catch (error) {
        setDadosLote(defaultDadosLote);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [setLoading],
  );

  async function reemitirCrlvs(id: number) {
    try {
      setMensagemErroReemitir("");
      const { data } = await msAw.post(`/licenciamento/lote/${id}/reconsulta`);

      setErroReemitir(false);
      return data;
    } catch (error: any) {
      setMensagemErroReemitir(error?.response?.data?.message ?? "");
      setErroReemitir(true);
    }
  }

  async function downloadCrlv(
    idLote: string,
    idsLicenciamentos: number[] = [],
    padraoRenomear: string = "{placa}_{ano}_CL",
  ) {
    setLoading(true);
    const ajuste = [
      filtrosDetalhes?.placa ? `placa=${filtrosDetalhes.placa}` : null,
      filtrosDetalhes?.renavam ? `renavam=${filtrosDetalhes.renavam}` : null,
      filtrosDetalhes?.exercicio
        ? `exercicio=${filtrosDetalhes.exercicio}`
        : null,
      filtrosDetalhes?.statusEmissao
        ? `status=${filtrosDetalhes.statusEmissao}`
        : null,
      idsLicenciamentos.length > 0
        ? `idsItensLoteLicenciamento=${idsLicenciamentos.join(",")}`
        : null,
    ];

    const queryParam = ajuste.filter((item) => item !== null).join("&");

    const urlDownload = `/licenciamento/lote/${idLote}/download?${queryParam}&padrao=${padraoRenomear}`;

    try {
      const response = await msAw.get(urlDownload, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/zip",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `crlvs_${idLote}.zip`);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toastErro(
        e?.response?.data?.message ??
          e?.message ??
          "Não foi possível fazer o download",
      );
    } finally {
      setLoading(false);
    }
  }

  const [showModalConfirmarEmissaoManual, setShowModalConfirmarEmissaoManual] =
    useState<boolean>(false);

  async function emissaoManual(
    idItemLoteLicenciamento: number,
    dados: IEmissaoManual,
    idLote: number,
  ) {
    setClearRowsDetalhes(false);
    setLoading(true);
    try {
      await msAw.post(
        `/licenciamento/item/${idItemLoteLicenciamento}/manual`,
        {
          ...dados,
          dataEmissao: converterDataHoraEua(dados.dataEmissao),
          extensao: dados.arquivo?.name.split(".")[1],
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      listarDetalhesLicenciamento(idLote);
      setShowModalConfirmarEmissaoManual(true);
      setClearRowsDetalhes(true);
    } catch (e: any) {
      toastErro(
        e?.response?.data?.message ??
          e?.message ??
          "Erro ao confirmar emissão manual",
      );
    } finally {
      setLoading(false);
    }
  }

  async function exportarCrlv(idEmissao: any, idsLicenciamentos: any[] = []) {
    const ajuste = [
      filtrosDetalhes?.placa ? `placa=${filtrosDetalhes.placa}` : null,
      filtrosDetalhes?.renavam ? `renavam=${filtrosDetalhes.renavam}` : null,
      filtrosDetalhes?.exercicio
        ? `exercicio=${filtrosDetalhes.exercicio}`
        : null,
      filtrosDetalhes?.statusEmissao
        ? `status=${filtrosDetalhes.statusEmissao}`
        : null,
      idsLicenciamentos.length > 0
        ? `idsLotesLicenciamentos=${idsLicenciamentos.join(",")}`
        : null,
    ];

    const queryParam = ajuste.filter((item) => item !== null).join("&");

    try {
      const { data } = await msAw.get(
        `/licenciamento/item/detalhes/${idEmissao}?${queryParam}&paginate=${Paginar.NAO}`,
      );
      setDadosExportar(
        data?.itens && Array.isArray(data.itens) ? data.itens : [],
      );
    } catch (e) {}
  }

  const onClickProcesso = async (id: number | string) => {
    setLoading(true);
    try {
      const { status, data } = await api.get(`/processos/param/${id}`);
      window.open(
        `${process.env.REACT_APP_DESPACHANTE_BASE_URL}/pages/processo/Processo.php?param=${data}`,
        "_blank",
      );
      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  return {
    listarSolicitacoesLicenciamento,
    setFiltros,
    loading,
    solicitacoes,
    setPage,
    setLimit,
    totalRows,
    limit,
    page,
    validarProcessosVincularLote,
    validarPlacasChassisVincularLote,
    selectedRows,
    setSelectedRows,
    clearRows,
    setClearRows,
    selectedRowsRemover,
    setSelectedRowsRemover,
    processosVincularLote,
    setProcessosVincularLote,
    listarProcessosVincularLote,
    selectedRowsDetalhes,
    setSelectedRowsDetalhes,
    clearRowsDetalhes,
    setClearRowsDetalhes,
    listarDetalhesLicenciamento,
    listaDetalhesLicenciamentos,
    buscarDadosLicenciamento,
    dadosLote,
    reemitirCrlvs,
    erroReemitir,
    setErroReemitir,
    downloadCrlv,
    showModalConfirmarEmissaoManual,
    setShowModalConfirmarEmissaoManual,
    emissaoManual,
    anoSolicitado,
    exportarCrlv,
    dadosExportar,
    salvarLote,
    filtrosDetalhes,
    mensagemErroReemitir,
    onClickProcesso,
  };
}
